/**
 * IP Table widget registry and column descriptors.
 *
 * Backend responses are now generic: each row is
 *   { ip, enrichments: { <slug>: { ...blob }, ... } }
 * where <slug> is a `webscout_*` enrichment slug. Only granted enrichments
 * appear as keys (present-when-granted, possibly empty).
 *
 * The registry maps each known slug to one-or-more column descriptors. Each
 * descriptor's `accessor` is a dot-path RELATIVE to `row.enrichments[slug]`
 * (or, for COMMON_COLUMNS, relative to the row itself).
 *
 * Unknown / sideloaded slugs (granted but not in the registry) get a single
 * generic fallback descriptor that renders the whole blob as key→value pills.
 *
 * Used by IpResultsTable, IpTableSidebar and useIpTableSettings.
 */

// Sideload config is entity-agnostic and lives in its own module; re-exported
// here for the existing IP-table importers.
import { SIDELOAD_ENRICHMENTS, SIDELOAD_SLUGS, slugLabel } from '@/config/enrichmentSideload'
export { SIDELOAD_ENRICHMENTS, SIDELOAD_SLUGS, slugLabel }

// Common columns live at the top level of the row (never inside enrichments).
// The identity column reads `selector` — the uniform selector-fetch row shape
// is { selector, parsed, enrichments } across every entity type.
export const COMMON_COLUMNS = [
  { id: 'ip', label: 'IP', width: 130, widget: 'IpWidget', accessor: 'selector', common: true },
]

// Per-slug widget descriptors. `accessor` is relative to row.enrichments[slug].
export const ENRICHMENT_WIDGETS = {
  webscout_geo: [
    { id: 'webscout_geo.location',         label: 'Location',         width: 90,  widget: 'Country',   accessor: 'location', field: 'location.country_iso', filterValues: countryCodes },
    { id: 'webscout_geo.hostnames',        label: 'Hostnames',        width: 200, widget: 'TagsBasic', accessor: 'hostnames' },
    { id: 'webscout_geo.network_type',     label: 'Network Type',     width: 120, widget: 'Text',      accessor: 'network.type' },
    { id: 'webscout_geo.company_name',     label: 'Company',          width: 150, widget: 'Text',      accessor: 'company.name' },
    { id: 'webscout_geo.company_business', label: 'Company Business', width: 200, widget: 'Tags',      accessor: 'company.business' },
  ],
  webscout_anonymization: [
    { id: 'webscout_anonymization', label: 'Anonymization', width: 140, widget: 'Anonymization', accessor: '', filterValues: anonProviderLabels },
  ],
  webscout_osint: [
    { id: 'webscout_osint.services', label: 'OSINT Mentions', width: 150, widget: 'OsintProviders', accessor: 'services', filterValues: osintProviderNames },
  ],
  webscout_observations: [
    { id: 'webscout_observations', label: 'Observations', width: 150, widget: 'Observations', accessor: '', filterAccessor: 'total_count' },
  ],
  webscout_signals: [
    { id: 'webscout_signals', label: 'Signals', width: 250, widget: 'Signals', accessor: 'signals', filterValues: signalTitles },
  ],
  abuseipdb: [
    { id: 'abuseipdb.score',   label: 'AbuseIP Score',   width: 120, widget: 'AbuseScore',   accessor: '', filterAccessor: 'abuse_confidence_score' },
    { id: 'abuseipdb.reports', label: 'AbuseIP Reports', width: 180, widget: 'AbuseReports', accessor: '', filterAccessor: 'total_reports' },
  ],
}

/**
 * Get nested value from object using dot notation. Fans out over arrays so
 * getValue({location: [{country_iso: 'US'}]}, 'location.country_iso') → ['US'].
 * @param {Object} obj
 * @param {string} path
 */
export function getValue(obj, path) {
  if (!path) return obj
  return path.split('.').reduce((acc, part) => {
    if (acc == null) return acc
    if (Array.isArray(acc)) return acc.map(item => (item == null ? item : item[part]))
    return acc[part]
  }, obj)
}

/**
 * All descriptors known to the registry, in canonical order
 * (common columns first, then each slug's widgets). This is the
 * persistence universe for show/hide + ordering.
 * @returns {Array<Object>}
 */
export function getAllWidgetDescriptors() {
  const out = [...COMMON_COLUMNS]
  for (const widgets of Object.values(ENRICHMENT_WIDGETS)) {
    out.push(...widgets)
  }
  return out
}

/**
 * Look up a descriptor by its id, across common columns and all slugs.
 * @param {string} id
 * @returns {Object|null}
 */
export function getDescriptorById(id) {
  return getAllWidgetDescriptors().find(d => d.id === id) || null
}

/**
 * Registry widgets for a given slug, or [] if the slug is unknown.
 * @param {string} slug
 * @returns {Array<Object>}
 */
export function getWidgetsForSlug(slug) {
  return ENRICHMENT_WIDGETS[slug] || []
}

/**
 * Build a generic fallback descriptor for a sideloaded / unknown slug.
 * Renders the whole enrichment blob as key→value pills.
 * @param {string} slug
 * @returns {Object}
 */
export function genericDescriptorForSlug(slug) {
  return {
    id: slug,
    label: slugLabel(slug),
    width: 220,
    widget: 'Generic',
    accessor: '',     // empty accessor → the whole enrichments[slug] blob
    slug,
    generic: true,
  }
}

/**
 * Clean, human-typeable filter token for a descriptor, derived from its label:
 * 'Network Type' → 'network_type', 'Location' → 'location', 'Company Business'
 * → 'company_business'. This is the field name used in the client-side filter
 * query (autocomplete + cell +/- emit + evaluation) so queries read like the
 * columns the user sees ('location:dk'), not internal slug paths
 * ('webscout_geo.location'). Resolve the value back through the descriptor
 * (resolveCellValue), which honors the descriptor's own accessor.
 * @param {Object} descriptor
 * @returns {string}
 */
export function filterKeyForDescriptor(descriptor) {
  if (!descriptor) return ''
  const label = descriptor.label || descriptor.id || ''
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || descriptor.id
}

/**
 * The set of enrichment slugs present across a set of rows
 * (union of Object.keys(row.enrichments) over all rows).
 * @param {Array<Object>} rows
 * @returns {string[]}
 */
export function presentSlugs(rows) {
  const set = new Set()
  for (const row of rows || []) {
    const enr = row?.enrichments
    if (enr && typeof enr === 'object') {
      for (const slug of Object.keys(enr)) set.add(slug)
    }
  }
  return [...set]
}

/**
 * The descriptors that are renderable for a set of rows: common columns plus,
 * per present slug, either its registry widgets or a single generic fallback.
 * @param {Array<Object>} rows
 * @returns {Array<Object>}
 */
export function getAvailableDescriptors(rows) {
  const out = [...COMMON_COLUMNS]
  for (const slug of presentSlugs(rows)) {
    const widgets = getWidgetsForSlug(slug)
    if (widgets.length > 0) out.push(...widgets)
    else out.push(genericDescriptorForSlug(slug))
  }
  return out
}

/**
 * Resolve the value a descriptor's widget should receive for a given row.
 * Common columns read from the row; everything else reads from the row's
 * enrichment blob for the descriptor's slug.
 * @param {Object} row
 * @param {Object} descriptor
 */
export function resolveCellValue(row, descriptor) {
  if (!descriptor) return undefined
  if (descriptor.common) return getValue(row, descriptor.accessor)
  const slug = descriptor.slug || descriptorSlug(descriptor.id)
  const blob = row?.enrichments?.[slug]
  if (!descriptor.accessor) return blob
  return getValue(blob, descriptor.accessor)
}

/**
 * The value a client-side filter should match a `field:value` term against.
 * Same as the rendered cell value, except for widgets that render a whole blob
 * but only expose part of it:
 *   `filterAccessor` — keys on one scalar (abuseipdb score/reports, observations
 *     count), so `abuseip_score:80` filters the score, not the whole blob (which
 *     would also match the report count, thumbnail url, etc.).
 *   `filterValues` — yields the clean values the widget actually displays
 *     (country codes, provider labels, signal titles). Matching the raw blob
 *     instead would match anything buried in it: an unquoted `location:in` would
 *     hit every row whose *city* contains "in".
 * Terms are matched against this by substring (unquoted) or leaf-equality
 * (quoted), so what it returns defines the column's filter vocabulary.
 * @param {Object} row
 * @param {Object} descriptor
 */
export function resolveFilterValue(row, descriptor) {
  const base = resolveCellValue(row, descriptor)
  if (descriptor?.filterAccessor) return getValue(base, descriptor.filterAccessor)
  if (descriptor?.filterValues) return descriptor.filterValues(base)
  return base
}

/**
 * The country codes for a Location cell — the values the Country widget shows.
 * The resolved value is a geo object (or an array of them) carrying city,
 * coordinates and more besides, so without this a whole-cell filter or a value
 * count would treat a latitude as a location.
 * @param {Object|Array} location - the descriptor's resolved `location` value
 * @returns {string[]}
 */
function countryCodes(location) {
  if (!location) return []
  const entries = Array.isArray(location) ? location : [location]
  return entries.map(e => e?.country_iso).filter(Boolean)
}

/**
 * The clean, matchable provider labels for an anonymization blob — the same
 * values the Anonymization widget displays. Used as a descriptor's `filterValues`
 * so a whole-cell +/- filters for/against the actual providers (works even for a
 * single provider, which the widget renders inline with no per-chip buttons).
 * Unknown-service flags (vpn/proxy/residential with no named service) have no
 * matchable string in the blob, so they're intentionally omitted.
 * @param {Object} blob - row.enrichments.webscout_anonymization
 * @returns {string[]}
 */
function anonProviderLabels(blob) {
  if (Array.isArray(blob?.services) && blob.services.length) {
    return blob.services.map(s => s.display_name || s.provider).filter(Boolean)
  }
  return []
}

/**
 * Clean, matchable provider names for the OSINT Mentions cell — the names the
 * widget shows (`display_name || provider`). `filterValues` receives the
 * descriptor's resolved value, which for this column is the `services` array.
 * @param {Array} services
 * @returns {string[]}
 */
function osintProviderNames(services) {
  return Array.isArray(services)
    ? services.map(s => s.display_name || s.provider).filter(Boolean)
    : []
}

/**
 * Signal titles for the Signals cell — the strings the widget shows. Receives
 * the resolved `signals` array.
 * @param {Array} signals
 * @returns {string[]}
 */
function signalTitles(signals) {
  return Array.isArray(signals) ? signals.map(s => s.title).filter(Boolean) : []
}

/**
 * Derive the owning slug from a descriptor id ('webscout_geo.location' →
 * 'webscout_geo', 'webscout_observations' → 'webscout_observations').
 * @param {string} id
 * @returns {string}
 */
export function descriptorSlug(id) {
  if (!id) return id
  return id.includes('.') ? id.slice(0, id.indexOf('.')) : id
}

/**
 * Get the label for a descriptor id, falling back to a humanized id.
 * @param {string} id
 * @returns {string}
 */
export function getFieldLabel(id) {
  const d = getDescriptorById(id)
  return d ? d.label : slugLabel(id)
}

/**
 * Preferred default column order, by descriptor id. Interleaves across slugs
 * to match the historical webscout layout. Any registry descriptor not listed
 * here is appended (in canonical order) so new widgets never silently vanish.
 * @type {string[]}
 */
export const DEFAULT_FIELD_ORDER = [
  'webscout_geo.location',
  'ip',
  'webscout_geo.hostnames',
  'webscout_observations',
  'webscout_anonymization',
  'webscout_osint.services',
  'webscout_signals',
  'abuseipdb.score',
  'abuseipdb.reports',
  'webscout_geo.network_type',
  'webscout_geo.company_name',
  'webscout_geo.company_business',
]

/**
 * Default field order: the preferred layout above, followed by any registry
 * descriptor ids not explicitly listed (in canonical order).
 * @returns {string[]}
 */
export function getDefaultFieldOrder() {
  const all = getAllWidgetDescriptors().map(d => d.id)
  const known = new Set(all)
  const preferred = DEFAULT_FIELD_ORDER.filter(id => known.has(id))
  const seen = new Set(preferred)
  return [...preferred, ...all.filter(id => !seen.has(id))]
}

/**
 * All known field ids (persistence universe).
 * @returns {string[]}
 */
export function getAllFieldKeys() {
  return getAllWidgetDescriptors().map(d => d.id)
}
